import React from 'react';
import {useNavigate} from 'react-router';

import {updateContent} from 'common/report/queries/app-api/content-client';
import {useTranslation} from 'common/i18n/hooks';
import {Content} from 'common/types/app-api/content';

import {TrackingEvent, TrackingSource} from '../tracking';
import {getContentTypeName, performContentAction} from '../helpers';

export type UseRenameContentParams = {
  projectId: string | number;
  source?: TrackingSource;
};

export function useRenameContent({projectId, source}: UseRenameContentParams) {
  const i18n = useTranslation();
  const navigate = useNavigate();

  return React.useCallback(
    (
      content: Content,
      {name, description}: {name: string; description: string},
      onSuccess?: (result: Content) => void,
    ) => {
      const entityTypeName = getContentTypeName(content, i18n);
      const nameChanged = content.name !== name;
      return performContentAction({
        content,
        action: () =>
          updateContent({
            content: {
              ...content,
              name,
              description,
            },
            projectId,
          }),
        onSuccess: (result) => {
          onSuccess?.(result);
        },
        successMessage: nameChanged ? i18n.t(`sidenav.rename-content`, {entityTypeName}) : undefined,
        errorMessage: nameChanged ? i18n.t(`sidenav.rename-content-error`, {entityTypeName}) : undefined,
        successTrackingEvent: TrackingEvent.Renamed,
        successTrackingProps: {
          name,
        },
        errorTrackingEvent: TrackingEvent.Renamed,
        errorTrackingProps: {
          name,
        },
        source,
      });
    },
    [i18n, navigate, projectId, source],
  );
}
